"use client";

import { useState } from "react";
import { FaChevronLeft, FaChartBar, FaChartLine } from "react-icons/fa";
import { TbChartCandle } from "react-icons/tb";
import { CheckIcon, ChevronRightIcon, PaletteIcon } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Card, CardContent } from "@/shadcn-components/ui/card";
import { Switch } from "@/shadcn-components/ui/switch";
import { Label } from "@/shadcn-components/ui/label";
import { Badge } from "@/shadcn-components/ui/badge";
import { Separator } from "@/shadcn-components/ui/separator";
import ColorsScreen from "./ColorsScreen";
import { useChartSettingsStore, ChartType, ChartFeatures } from "@/lib/chart-settings";
import { useSettingsStore } from "@/lib/settings";
import { clearDesk } from "@/lib/chart-desk";

interface ChartsSettingsScreenProps {
  onBack: () => void;
}

// Chart types configuration
const CHART_TYPES = [
  { id: "bars" as ChartType, name: "Bar Chart", icon: FaChartBar },
  {
    id: "candlesticks" as ChartType,
    name: "Candlesticks",
    icon: TbChartCandle,
  },
  { id: "line" as ChartType, name: "Line Chart", icon: FaChartLine },
];

// Settings configuration
type SettingItem = {
  key: keyof ChartFeatures | "oneClickTrading";
  label: string;
  description?: string;
  variant?: "default" | "pro";
  isSettingsStore?: boolean;
};

const SETTINGS_SECTIONS = {
  display: {
    title: "Display Options",
    items: [
      { key: "volumes", label: "Volumes", description: "Show volume bars" },
      { key: "tickVolumes", label: "Tick Volumes", description: "Display tick volume data" },
      { key: "askPriceLine", label: "Ask Price Line", description: "Show ask price level", variant: "pro" },
      { key: "periodSeparators", label: "Period Separators", description: "Vertical lines between periods" },
    ] as SettingItem[]
  },
  trading: {
    title: "Trading Features",
    items: [
      { key: "countdownToBarClose", label: "Bar Close Countdown", description: "Show time to next bar" },
      { key: "tradeOrders", label: "Trade Orders", description: "Display pending orders" },
      { key: "tradePositions", label: "Trade Positions", description: "Show open positions" },
      { key: "slTpLevels", label: "SL/TP Levels", description: "Stop Loss & Take Profit lines" },
      { key: "tradeHistory", label: "Trade History", description: "Historical trade marks" },
      { key: "isPipsVisible", label: "Show Pips", description: "Show pips instead of profit" },
      { key: "oneClickTrading", label: "One Click Trading", description: "Close trades without confirmation", isSettingsStore: true },
      { key: "autoSlTpModify", label: "Auto SL/TP Modify", description: "Auto modify trade after drop line" },
    ] as SettingItem[]
  },
  interface: {
    title: "Interface Settings",
    items: [
      { key: "ohlc", label: "OHLC Data", description: "Open, High, Low, Close values" },
      { key: "preloadChartsData", label: "Preload Data", description: "Faster chart loading", variant: "pro" },
    ] as SettingItem[]
  }
};

export default function ChartsSettingsScreen({
  onBack,
}: ChartsSettingsScreenProps) {
  const [showColors, setShowColors] = useState(false);
  
  // Use chart settings store for all settings
  const chartSettings = useChartSettingsStore((state) => state.chartSettings);
  const updateChartSetting = useChartSettingsStore((state) => state.updateChartSetting);
  const updateChartFeature = useChartSettingsStore((state) => state.updateChartFeature);
  
  // Use settings store for oneClickTrading
  const oneClickTrading = useSettingsStore((state) => state.settings.oneClickTrading);
  const updateSetting = useSettingsStore((state) => state.updateSetting);

  const toggleSetting = (key: keyof ChartFeatures | "oneClickTrading") => {
    if (key === "oneClickTrading") {
      updateSetting("oneClickTrading", !oneClickTrading);
    } else {
      updateChartFeature(key, !chartSettings.features[key]);
    }
  };

  const [deskReset, setDeskReset] = useState(false);

  const handleChartTypeChange = (type: ChartType) => {
    updateChartSetting('chartType', type);
  };

  // Show Colors screen if clicked
  if (showColors) {
    return <ColorsScreen onBack={() => setShowColors(false)} />;
  }

  return (
    <div className="h-full bg-background text-foreground flex flex-col">
      {/* Header according to developer-guidelines */}
      <div className="flex items-center justify-between px-4 py-3 h-14 bg-background border-b border-border">
        <Button variant="ghost" size="icon" onClick={onBack} className="h-10 w-10">
          <FaChevronLeft className="text-blue-400 text-xl" />
        </Button>
        <h1 className="text-xl font-medium text-foreground">Charts</h1>
        {/* Empty div for spacing balance */}
        <div className="w-10"></div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Chart Types Section */}
        <div className="px-4 pt-4 pb-3">
          <h2 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
            CHART TYPE
          </h2>
          <Card>
            <CardContent className="p-0">
              {CHART_TYPES.map((type, index) => (
                <div key={type.id}>
                  <Button
                    variant="ghost"
                    onClick={() => handleChartTypeChange(type.id)}
                    className={`w-full h-auto p-3 justify-between hover:bg-muted/50 rounded-none
                      ${index === 0 ? "rounded-t-lg" : ""}
                      ${index === CHART_TYPES.length - 1 ? "rounded-b-lg" : ""}
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <type.icon className="text-blue-400 text-lg" />
                      <span className="text-base font-medium">{type.name}</span>
                    </div>
                    {chartSettings.chartType === type.id && (
                      <CheckIcon className="h-5 w-5 text-blue-400" />
                    )}
                  </Button>
                  {index < CHART_TYPES.length - 1 && <Separator />}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Settings Sections */}
        {Object.entries(SETTINGS_SECTIONS).map(([sectionKey, section]) => (
          <div key={sectionKey} className="px-4 pb-3">
            <h2 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
              {section.title}
            </h2>
            <Card>
              <CardContent className="p-0">
                {section.items.map((item, index) => (
                  <div key={item.key}>
                    <div className={`flex items-center justify-between p-3
                      ${index === 0 ? "rounded-t-lg" : ""}
                      ${index === section.items.length - 1 ? "rounded-b-lg" : ""}
                    `}>
                      <div className="flex-1 min-w-0 mr-3">
                        <div className="flex items-center gap-2">
                          <Label 
                            htmlFor={item.key} 
                            className="text-base font-medium cursor-pointer"
                          >
                            {item.label}
                          </Label>
                          {item.variant === "pro" && (
                            <Badge variant="secondary" className="text-xs">
                              PRO
                            </Badge>
                          )}
                        </div>
                        {item.description && (
                          <p className="text-sm text-muted-foreground mt-0.5">
                            {item.description}
                          </p>
                        )}
                      </div>
                      <Switch
                        id={item.key}
                        checked={item.isSettingsStore && item.key === "oneClickTrading" 
                          ? oneClickTrading 
                          : chartSettings.features[item.key as keyof ChartFeatures]}
                        onCheckedChange={() => toggleSetting(item.key)}
                      />
                    </div>
                    {index < section.items.length - 1 && <Separator />}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        ))}

        {/* Colors Section */}
        <div className="px-4 pb-4">
          <h2 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
            APPEARANCE
          </h2>
          <Card>
            <CardContent className="p-0">
              <Button
                variant="ghost"
                onClick={() => setShowColors(true)}
                className="w-full h-auto p-3 justify-between hover:bg-muted/50"
              >
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <PaletteIcon className="h-6 w-6 text-blue-400" />
                    <div className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-gradient-to-br from-red-400 via-yellow-400 to-blue-400"></div>
                  </div>
                  <div className="text-left">
                    <div className="text-base font-medium">Colors</div>
                    <div className="text-sm text-muted-foreground">
                      Customize chart appearance
                    </div>
                  </div>
                </div>
                <ChevronRightIcon className="h-5 w-5 text-muted-foreground" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Chart desk — proposal 74 */}
        <div className="px-4 pb-4">
          <h2 className="text-muted-foreground text-sm uppercase font-medium tracking-wide mb-3">
            CHART DESK
          </h2>
          <Card>
            <CardContent className="p-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-base font-medium">Reset chart desk</div>
                  <div className="text-sm text-muted-foreground">
                    {deskReset
                      ? "Cleared — charts will start empty next launch."
                      : "Forget the symbol and timeframe remembered for each chart panel."}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={deskReset}
                  onClick={() => {
                    clearDesk();
                    setDeskReset(true);
                  }}
                >
                  {deskReset ? "Cleared" : "Reset"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bottom spacing */}
        <div className="h-6"></div>
      </div>
    </div>
  );
}